import type { ArenaFilters, ArenaPair, ArenaSample, ArenaVote } from "./types";

export function applyFilters(samples: ArenaSample[], filters: ArenaFilters): ArenaSample[] {
  return samples.filter((sample) => {
    if (filters.language !== "all" && sample.language !== filters.language) return false;
    if (filters.category !== "all" && sample.category !== filters.category) return false;
    if (filters.provider !== "all" && sample.provider !== filters.provider) return false;
    return true;
  });
}

export function samplePair(
  samples: ArenaSample[],
  votes: ArenaVote[],
  filters: ArenaFilters,
  previousPairKey?: string,
): ArenaPair | null {
  const filtered = applyFilters(samples, filters);
  const grouped = new Map<string, ArenaSample[]>();

  for (const sample of filtered) {
    const key = `${sample.promptId}::${sample.language}::${sample.requestedSpeed}`;
    grouped.set(key, [...(grouped.get(key) ?? []), sample]);
  }

  const pairCounts = buildPairCounts(votes);
  const candidates: ArenaPair[] = [];

  for (const group of grouped.values()) {
    for (let i = 0; i < group.length; i += 1) {
      for (let j = i + 1; j < group.length; j += 1) {
        const left = group[i];
        const right = group[j];
        if (left.provider === right.provider && left.model === right.model) continue;
        if (left.id === right.id) continue;
        // Only pair same gender — cross-gender comparisons are unfair
        if (left.gender && right.gender && left.gender !== right.gender) continue;
        const pairKey = makePairKey(left.id, right.id);
        candidates.push(Math.random() > 0.5 ? { left, right, pairKey } : { left: right, right: left, pairKey });
      }
    }
  }

  if (candidates.length === 0) return null;

  const minCount = Math.min(...candidates.map((pair) => pairCounts.get(pair.pairKey) ?? 0));
  const leastSampled = candidates.filter((pair) => (pairCounts.get(pair.pairKey) ?? 0) === minCount);
  const withoutImmediateRepeat = leastSampled.filter((pair) => pair.pairKey !== previousPairKey);
  const pool = withoutImmediateRepeat.length > 0 ? withoutImmediateRepeat : leastSampled;
  return pool[Math.floor(Math.random() * pool.length)];
}

export function makePairKey(leftSampleId: string, rightSampleId: string): string {
  return [leftSampleId, rightSampleId].sort().join("::");
}

export function buildCoverageRows(
  samples: ArenaSample[],
  votes: ArenaVote[],
): Array<{ language: string; provider: string; comparisons: number; samples: number }> {
  const sampleById = new Map(samples.map((sample) => [sample.id, sample]));
  const rows = new Map<string, { language: string; provider: string; comparisons: number; samples: number }>();

  for (const sample of samples) {
    const key = `${sample.language}::${sample.provider}`;
    const row = rows.get(key) ?? { language: sample.language, provider: sample.provider, comparisons: 0, samples: 0 };
    row.samples += 1;
    rows.set(key, row);
  }

  for (const vote of votes) {
    if (vote.result === "skip") continue;
    for (const sampleId of [vote.leftSampleId, vote.rightSampleId]) {
      const sample = sampleById.get(sampleId);
      if (!sample) continue;
      const key = `${sample.language}::${sample.provider}`;
      const row = rows.get(key);
      if (row) row.comparisons += 1;
    }
  }

  return [...rows.values()].sort((a, b) => a.comparisons - b.comparisons || a.language.localeCompare(b.language));
}

function buildPairCounts(votes: ArenaVote[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const vote of votes) {
    if (vote.result === "skip") continue;
    const key = makePairKey(vote.leftSampleId, vote.rightSampleId);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}
